import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { UserTypes } from '../../shared/enum/user-types.enum';
import { IAppState } from '../../store/interface/app.state.interface';
import { Store } from '@ngrx/store';
import { combineLatest, Subscription } from 'rxjs';
import { IUser } from '../../user/interfaces/user.interface';
import { IMenuItem } from '../interfaces/layout.interface';
import { PermissionKey } from '../../shared/enum/permission-key.enum';
import { PermissionAccessService } from '../../shared/services/permission-access.service';
import { SETTING_MENU_ITEMS } from '../../setting/constant/setting-menu';
import { isSettingsPageOpen } from '../../setting/util/settings-landing';

@Component({
    standalone: false,
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './sidebar.component.scss',
})
export class SidebarComponent implements OnInit {
    model: MenuItem[] = [];

    /*=====================================
      Settings sits on its own at the foot of the sidebar rather than at the end
      of the list. It is a destination you leave the day's work to visit, not
      another section of it, and pinning it means its position does not shift as
      menu groups appear and disappear per user type.
    ======================================*/
    bottomModel: MenuItem[] = [];
    subscriptions: Subscription[] = [];
    loggedInUser: IUser | null = null;
    pendingContactEnquiries = 0;
    pendingAndProcessingOrders = 0;
    constructor(
        private store: Store<IAppState>,
        private permissionAccess: PermissionAccessService,
    ) {}

    ngOnInit(): void {
        this.subscriptions.push(
            combineLatest([
                this.store.select((state) => state.dashboard.counts),
                this.store.select((state) => state.user.loggedInUser),
            ]).subscribe(([counts, user]) => {
                this.pendingContactEnquiries = counts.pendingContactEnquiries;
                this.pendingAndProcessingOrders = counts.pendingAndProcessingOrders;
                this.loggedInUser = user;

                this.setSidebarModel();
            }),
        );
    }

    setSidebarModel() {
        const rawModel: IMenuItem[] = [
            {
                items: [
                    {
                        label: 'Home',
                        icon: 'pi pi-home',
                        routerLink: ['/'],
                        testId: 'sidebar-home-link',
                    },
                ],
            },

            {
                items: [
                    {
                        label: 'Orders',
                        icon: 'pi pi-receipt',
                        userTypes: [UserTypes.TENANT_USER, UserTypes.TT_USER],
                        requiresShoppingStore: true,
                        tenantRequired: true,
                        testId: 'sidebar-orders-menu-trigger',
                        badge:
                            this.pendingAndProcessingOrders > 0
                                ? String(this.pendingAndProcessingOrders)
                                : undefined,
                        items: [
                            {
                                label: 'Orders',
                                icon: 'pi pi-list',
                                routerLink: ['/order'],
                                permission: PermissionKey.ORDERS,
                                testId: 'sidebar-orders-link',
                            },
                            {
                                label: 'Abandoned carts',
                                icon: 'pi pi-shopping-cart',
                                routerLink: ['/abandoned-cart'],
                                permission: PermissionKey.ORDERS,
                                testId: 'sidebar-abandoned-carts-link',
                            },
                            /*=====================================
                              Booking a courier for an order is order work. There
                              is no Fulfilment group: couriers, zones and rates are
                              three sections of one Store Settings page, not screens
                              of their own.
                            ======================================*/
                            {
                                label: 'Shipments',
                                icon: 'pi pi-box',
                                routerLink: ['/courier/book-shipment'],
                                permission: PermissionKey.FULFILMENT,
                                testId: 'sidebar-shipments-link',
                            },
                        ],
                    },
                ],
            },

            {
                items: [
                    {
                        label: 'Products',
                        icon: 'pi pi-shopping-bag',
                        userTypes: [UserTypes.TENANT_USER, UserTypes.TT_USER],
                        requiresShoppingStore: true,
                        tenantRequired: true,
                        testId: 'sidebar-products-menu-trigger',
                        items: [
                            {
                                label: 'Products',
                                icon: 'pi pi-list',
                                routerLink: ['/product'],
                                permission: PermissionKey.PRODUCTS,
                                testId: 'sidebar-products-link',
                            },
                            {
                                label: 'Categories',
                                icon: 'pi pi-sitemap',
                                routerLink: ['/category'],
                                permission: PermissionKey.PRODUCTS,
                                testId: 'sidebar-categories-link',
                            },
                            {
                                label: 'Brands',
                                icon: 'pi pi-bookmark',
                                routerLink: ['/brand'],
                                permission: PermissionKey.PRODUCTS,
                                testId: 'sidebar-brands-link',
                            },
                            {
                                label: 'Tags',
                                icon: 'pi pi-tags',
                                routerLink: ['/product/tag'],
                                permission: PermissionKey.PRODUCTS,
                                testId: 'sidebar-tags-link',
                            },
                            {
                                label: 'Reviews',
                                icon: 'pi pi-star',
                                routerLink: ['/reviews'],
                                permission: PermissionKey.REVIEWS,
                                testId: 'sidebar-reviews-link',
                            },
                        ],
                    },
                ],
            },

            {
                items: [
                    {
                        label: 'Customers',
                        icon: 'pi pi-users',
                        userTypes: [UserTypes.TENANT_USER, UserTypes.TT_USER],
                        tenantRequired: true,
                        testId: 'sidebar-customers-menu-trigger',
                        /*=====================================
                          The enquiry count sits on the group as well as on the
                          row it belongs to. Contact Enquiries was top level and
                          carried this badge in the open; nested under a collapsed
                          group, the count would only ever be seen by a merchant
                          who had already gone looking for it.
                        ======================================*/
                        badge:
                            this.pendingContactEnquiries > 0
                                ? String(this.pendingContactEnquiries)
                                : undefined,
                        items: [
                            /*=====================================
                              The shop-only flag sits on the child, not the group.
                              Enquiries and messages arrive through the website and
                              keep arriving with the shopping store switched off, so
                              hiding the whole group would lose them.
                            ======================================*/
                            {
                                label: 'Customers',
                                icon: 'pi pi-user',
                                requiresShoppingStore: true,
                                routerLink: ['/customer'],
                                permission: PermissionKey.CUSTOMERS,
                                testId: 'sidebar-customers-link',
                            },
                            {
                                label: 'Enquiries & messages',
                                icon: 'pi pi-address-book',
                                routerLink: ['/contact-enquiry'],
                                permission: PermissionKey.ENQUIRIES,
                                testId: 'sidebar-enquiries-link',
                                badge:
                                    this.pendingContactEnquiries > 0
                                        ? String(this.pendingContactEnquiries)
                                        : undefined,
                            },
                        ],
                    },
                ],
            },

            {
                items: [
                    {
                        label: 'Marketing',
                        icon: 'pi pi-megaphone',
                        userTypes: [UserTypes.TENANT_USER, UserTypes.TT_USER],
                        tenantRequired: true,
                        testId: 'sidebar-marketing-menu-trigger',
                        items: [
                            {
                                label: 'Coupons',
                                icon: 'pi pi-percentage',
                                requiresShoppingStore: true,
                                routerLink: ['/coupon'],
                                permission: PermissionKey.MARKETING,
                                testId: 'sidebar-coupons-link',
                            },
                            {
                                label: 'Discounts',
                                icon: 'pi pi-tag',
                                requiresShoppingStore: true,
                                routerLink: ['/discount'],
                                permission: PermissionKey.MARKETING,
                                testId: 'sidebar-discounts-link',
                            },
                            {
                                label: 'Announcements',
                                icon: 'pi pi-bell',
                                routerLink: ['/flash-news'],
                                permission: PermissionKey.MARKETING,
                                testId: 'sidebar-announcements-link',
                            },
                        ],
                    },
                ],
            },

            {
                items: [
                    {
                        label: 'Online Store',
                        icon: 'pi pi-globe',
                        userTypes: [UserTypes.TENANT_USER, UserTypes.TT_USER],
                        tenantRequired: true,
                        testId: 'sidebar-online-store-menu-trigger',
                        items: [
                            {
                                label: 'Pages',
                                icon: 'pi pi-file',
                                routerLink: ['/page'],
                                permission: PermissionKey.ONLINE_STORE,
                                testId: 'sidebar-pages-link',
                            },
                            {
                                label: 'Page redirects',
                                icon: 'pi pi-directions',
                                routerLink: ['/page/redirects'],
                                permission: PermissionKey.ONLINE_STORE,
                                testId: 'sidebar-page-redirects-link',
                            },
                            {
                                label: 'Blocks',
                                icon: 'pi pi-th-large',
                                routerLink: ['/block'],
                                permission: PermissionKey.ONLINE_STORE,
                                testId: 'sidebar-blocks-link',
                            },
                            {
                                label: 'Menus',
                                icon: 'pi pi-bars',
                                testId: 'sidebar-menus-menu-trigger',
                                items: [
                                    {
                                        label: 'Header menu',
                                        icon: 'pi pi-arrow-up',
                                        routerLink: ['/menu/header'],
                                        permission: PermissionKey.ONLINE_STORE,
                                        testId: 'sidebar-header-menu-link',
                                    },
                                    {
                                        label: 'Footer menu',
                                        icon: 'pi pi-arrow-down',
                                        routerLink: ['/menu/footer'],
                                        permission: PermissionKey.ONLINE_STORE,
                                        testId: 'sidebar-footer-menu-link',
                                    },
                                ],
                            },
                            /*=====================================
                              Forms carried userTypes:[TT_USER] until this story, so
                              no merchant had ever seen it. Its screens all exist.
                            ======================================*/
                            {
                                label: 'Forms',
                                icon: 'pi pi-pencil',
                                routerLink: ['/forms'],
                                permission: PermissionKey.ONLINE_STORE,
                                testId: 'sidebar-forms-link',
                            },
                        ],
                    },
                ],
            },

            // Platform console - a TriATech User with no tenant selected. The guard
            // on these routes asks the same question this flag does; see
            // core/guard/system-user.guard.ts.
            {
                items: [
                    {
                        label: 'Tenants',
                        icon: 'pi pi-building',
                        userTypes: [UserTypes.TT_USER],
                        tenantRequired: false,
                        testId: 'sidebar-tenants-menu-trigger',
                        items: [
                            {
                                label: 'Tenants',
                                icon: 'pi pi-list',
                                routerLink: ['/tenant'],
                                permission: PermissionKey.PLATFORM_TENANTS,
                                testId: 'sidebar-tenants-list-link',
                            },
                            {
                                label: 'Demo tenants',
                                icon: 'pi pi-clone',
                                routerLink: ['/tenant/demo-list'],
                                permission: PermissionKey.PLATFORM_TENANTS,
                            },
                        ],
                    },
                ],
            },

            {
                items: [
                    {
                        label: 'People',
                        icon: 'pi pi-users',
                        userTypes: [UserTypes.TT_USER],
                        tenantRequired: false,
                        testId: 'sidebar-people-menu-trigger',
                        items: [
                            {
                                label: 'Users',
                                icon: 'pi pi-user',
                                routerLink: ['/user'],
                                permission: PermissionKey.PLATFORM_PEOPLE,
                                testId: 'sidebar-user-list-link',
                            },
                            {
                                label: 'Roles',
                                icon: 'pi pi-id-card',
                                routerLink: ['/role'],
                                permission: PermissionKey.PLATFORM_PEOPLE,
                                testId: 'sidebar-roles-list-link',
                            },
                        ],
                    },
                ],
            },

            /*=====================================
              Themes and Home are links, not groups. Each has exactly one screen,
              and a group of one is a heading pretending to be structure. Colour
              schemes are dialogs inside the merchant's store customiser, not a
              platform screen.
            ======================================*/
            {
                items: [
                    {
                        label: 'Themes',
                        icon: 'pi pi-palette',
                        userTypes: [UserTypes.TT_USER],
                        tenantRequired: false,
                        routerLink: ['/theme'],
                        permission: PermissionKey.PLATFORM_THEMES,
                        testId: 'sidebar-themes-link',
                    },
                ],
            },

            /*=====================================
              Reference data is today's System Settings, flattened. The Financial
              and Location headings were grouping for its own sake. Tax is Store
              tax here: it is the merchant's tax on their own orders, and
              00-saas-billing has a second, unrelated tax screen.
            ======================================*/
            {
                items: [
                    {
                        label: 'Reference data',
                        icon: 'pi pi-database',
                        userTypes: [UserTypes.TT_USER],
                        tenantRequired: false,
                        testId: 'sidebar-reference-data-menu-trigger',
                        items: [
                            {
                                label: 'Store tax',
                                icon: 'pi pi-percentage',
                                routerLink: ['/tax'],
                                permission: PermissionKey.PLATFORM_REFERENCE_DATA,
                            },
                            {
                                label: 'Currency',
                                icon: 'pi pi-dollar',
                                routerLink: ['/currency'],
                                permission: PermissionKey.PLATFORM_REFERENCE_DATA,
                            },
                            {
                                label: 'States',
                                icon: 'pi pi-map',
                                routerLink: ['/location/state'],
                                permission: PermissionKey.PLATFORM_REFERENCE_DATA,
                            },
                            {
                                label: 'Cities',
                                icon: 'pi pi-map-marker',
                                routerLink: ['/location/city'],
                                permission: PermissionKey.PLATFORM_REFERENCE_DATA,
                            },
                            {
                                label: 'Areas',
                                icon: 'pi pi-compass',
                                routerLink: ['/location/area'],
                                permission: PermissionKey.PLATFORM_REFERENCE_DATA,
                            },
                            {
                                label: 'Shipping methods',
                                icon: 'pi pi-truck',
                                routerLink: ['/shipping-method'],
                                permission: PermissionKey.PLATFORM_REFERENCE_DATA,
                            },
                            {
                                label: 'Payment methods',
                                icon: 'pi pi-credit-card',
                                routerLink: ['/payment-gateway'],
                                permission: PermissionKey.PLATFORM_REFERENCE_DATA,
                            },
                            {
                                label: 'Lists of values',
                                icon: 'pi pi-sliders-h',
                                routerLink: ['/manage-list-of-value'],
                                permission: PermissionKey.PLATFORM_REFERENCE_DATA,
                            },
                        ],
                    },
                ],
            },
        ];

        const rawBottomModel: MenuItem[] = [
            {
                items: [
                    {
                        label: 'Settings',
                        icon: 'pi pi-cog',
                        routerLink: ['/settings'],
                        userTypes: [UserTypes.TENANT_USER, UserTypes.TT_USER],
                        tenantRequired: true,
                        requiresAnyPermission: SETTING_MENU_ITEMS.filter((item) =>
                            isSettingsPageOpen(item, this.permissionAccess.isSellingEnabled),
                        ).map((item) => item.permission),
                        testId: 'sidebar-settings-link',
                    },
                ],
            },
        ];

        this.model = this.filterMenuItems(rawModel);
        this.bottomModel = this.filterMenuItems(rawBottomModel);
    }

    filterMenuItems(items: MenuItem[]): MenuItem[] {
        return items.reduce<MenuItem[]>((acc, item) => {
            if (!this.shouldDisplay(item)) {
                return acc;
            }
            if (item.items) {
                const filteredChildren = this.filterMenuItems(item.items);
                if (filteredChildren.length > 0) {
                    acc.push({ ...item, items: filteredChildren });
                } else if (item.routerLink) {
                    /*=====================================
                      A group whose children all filter out is dropped, not kept
                      as an empty shell. It only survives if it is a destination
                      in its own right - it has a routerLink - in which case what
                      is left is a plain link.

                      Keeping it used to leave one label-less <li> per hidden
                      group. The template stopped drawing their headings, so they
                      are invisible, but a merchant still carries five of them
                      (User, Role, Tenant, System Settings, Theme) at the foot of
                      the menu, each with its own margin.
                    ======================================*/
                    acc.push((({ items: _items, ...rest }) => rest)(item));
                }
            } else {
                acc.push(item);
            }
            return acc;
        }, []);
    }
    shouldDisplay(menu: MenuItem): boolean {
        const user = this.loggedInUser;

        if (!user) return false;

        const item = menu as IMenuItem;
        if (item.userTypes?.length && !item.userTypes.includes(user.userType?.key ?? '')) {
            return false;
        }
        if (item.tenantRequired && !user.tenant?.id) return false;
        if (item.tenantRequired === false && user.tenant?.id) return false;

        /*=====================================
          A shop-only entry is hidden once the merchant turns the
          shopping store off in Settings > Store. Only an explicit
          false hides it - an undefined flag means the tenant was
          loaded without the column, which must not empty the menu.
        ======================================*/
        if (item.requiresShoppingStore && user.tenant?.enableShoppingStore === false) return false;

        if (item.permission && !this.permissionAccess.hasPermission(item.permission)) return false;

        /*=====================================
          Settings is a destination, not a group, so filterMenuItems cannot
          empty it the way it empties a group whose children have all gone. It
          holds ten entries under five permissions, and a role holding none of
          them has nothing to open.
        ======================================*/
        if (
            item.requiresAnyPermission?.length &&
            !item.requiresAnyPermission.some((permission) =>
                this.permissionAccess.hasPermission(permission),
            )
        ) {
            return false;
        }

        return true;
    }
}
